/**
 * Single source of truth for the three tracking modes: names, one-liners and how a mode is derived.
 * Stored agent types stay general | restricted | silent; mode ids stay as the backend knows them.
 * Change a user-facing name here and nowhere else.
 */
export type TrackingModeId = 'employee_controlled' | 'admin_controlled' | 'hidden';
export type TrackingModeVersionType = 'general' | 'restricted' | 'silent';

export interface TrackingModeInfo {
  id: TrackingModeId;
  versionType: TrackingModeVersionType;
  label: string;
  /** One line: visibility first, then who starts tracking. */
  summary: string;
}

export const TRACKING_MODES: Record<TrackingModeId, TrackingModeInfo> = {
  employee_controlled: {
    id: 'employee_controlled',
    versionType: 'general',
    label: 'Manual',
    summary: 'Visible. They start and stop tracking themselves.',
  },
  admin_controlled: {
    id: 'admin_controlled',
    versionType: 'restricted',
    label: 'Automatic',
    summary: 'Visible. Tracking runs automatically and they can’t start or stop it.',
  },
  hidden: {
    id: 'hidden',
    versionType: 'silent',
    label: 'Hidden',
    summary: 'Not visible. Tracking runs automatically, with no sign-in.',
  },
};

/** Labels by mode id, for templates: {{ modeLabels.hidden }}. */
export const TRACKING_MODE_LABELS: Record<TrackingModeId, string> = {
  employee_controlled: TRACKING_MODES.employee_controlled.label,
  admin_controlled: TRACKING_MODES.admin_controlled.label,
  hidden: TRACKING_MODES.hidden.label,
};

export function trackingModeIdForVersionType(
  vt: string | null | undefined
): TrackingModeId {
  switch (vt) {
    case 'silent':
      return 'hidden';
    case 'restricted':
      return 'admin_controlled';
    case 'general':
    default:
      return 'employee_controlled';
  }
}

export function trackingModeLabel(id: TrackingModeId): string {
  return TRACKING_MODES[id].label;
}

export function trackingModeLabelForVersionType(
  vt: string | null | undefined
): string {
  return trackingModeLabel(trackingModeIdForVersionType(vt));
}

/**
 * Mode of an installed agent from the first letter of its application type ('S' silent, 'G' general).
 * General agents are Admin Controlled when automatic tracking is on, read from the employee's own
 * settings if personalised, otherwise from the company settings.
 * Returns null when the agent type is unknown, or a general agent has no personalise setting.
 */
export function trackingModeIdForAgent(
  agentLetter: string | null | undefined,
  employeeSettings?: any,
  globalSettings?: any
): TrackingModeId | null {
  if (agentLetter === 'S') {
    return 'hidden';
  }
  if (agentLetter !== 'G') {
    return null;
  }
  const personalised = employeeSettings?.ss_personalizeSettings;
  if (personalised === 'true') {
    return employeeSettings?.ss_automatic_tracking === 'true' ? 'admin_controlled' : 'employee_controlled';
  }
  if (personalised === 'false') {
    return globalSettings?.ss_automatic_tracking === 'true' ? 'admin_controlled' : 'employee_controlled';
  }
  return null;
}
